import { useMemo, useEffect } from 'react';
import {
  SimpleDate,
  fromMoment,
  now as getSimpleDateNow,
  toMomentPortalTz,
} from 'foundations-components/transitional/utils/SimpleDate';
import {
  isDueDateTimeValid,
  isToday,
  getMinTimeForToday,
  getCurrentMinutes,
} from '../utils/dueDate';

interface UseDueDateValidationOptions {
  dueDate: SimpleDate | undefined;
  dueTime: number | undefined;
  setIsDueDateValid: (isValid: boolean) => void;
}

const ELEVEN_THIRTY_PM_IN_MINUTES = 23 * 60 + 30;

export const useDueDateValidation = ({
  dueDate,
  dueTime,
  setIsDueDateValid,
}: UseDueDateValidationOptions) => {
  const minSelectableDate = useMemo(() => {
    const currentMinutes = getCurrentMinutes();
    // Prevent current day being selected if it's after 11:30pm
    if (currentMinutes >= ELEVEN_THIRTY_PM_IN_MINUTES) {
      const tomorrow = toMomentPortalTz(getSimpleDateNow('portalTz')).add(
        1,
        'day'
      );
      return fromMoment(tomorrow);
    }

    return getSimpleDateNow('portalTz');
  }, []);

  const maxSelectableDate = useMemo(() => {
    const today = getSimpleDateNow('portalTz');
    const oneYearFromNow = toMomentPortalTz(today).add(1, 'year');
    return fromMoment(oneYearFromNow);
  }, []);

  const minTimeForDate = useMemo(() => {
    if (isToday(dueDate)) {
      return getMinTimeForToday();
    }
    return undefined;
  }, [dueDate]);

  useEffect(() => {
    const isValid = isDueDateTimeValid(dueDate, dueTime);
    setIsDueDateValid(isValid);
  }, [dueDate, dueTime, setIsDueDateValid]);

  return {
    minSelectableDate,
    maxSelectableDate,
    minTimeForDate,
  };
};
